/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, EventEmitter, Inject, Input, Output } from '@angular/core';

import { SlicedDataSource, SortDirection } from '@gh/core-data';
import { UI_ERROR_HANDLER, UiErrorHandler } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';

import {
  AgGridEvent,
  GridApi,
  GridOptions,
  IDatasource,
  IGetRowsParams,
  IRowNode,
} from 'ag-grid-community';

import { GRID_EVENT } from '../../grid-public-events';
import { assign, get, isNil } from 'lodash';

import { GridSelectedRowChangeEvent } from '../../grid-events';
import { FocusedCell } from '../../state/grid-state.interfaces';
import {
  GRID_FEATURE,
  GridFeature,
  GridFeatureInitParams,
  GridFeatureReadyParams,
} from '../feature.interfaces';

export interface GridInfiniteScroll {
  refresh(): void;
}

export interface GridRowTotalChangeEvent {
  total: number;
}

export interface GridDataExistEvent {
  exists: boolean;
}

/**
 * This directive encapsulates infinite scroll behaviour of grid component
 */
@Directive({
  standalone: false,
  selector: '[ghGridInfiniteScroll]',
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridInfiniteScrollDirective,
    multi: true,
  }],
  exportAs: 'ghGridInfiniteScroll',
})
export class GridInfiniteScrollDirective implements GridFeature, GridInfiniteScroll {

  order = 5;

  @Input() sorting?: boolean;

  @Output() focusedCellChange = new EventEmitter<FocusedCell>();
  @Output() selectedRowChange = new EventEmitter<GridSelectedRowChangeEvent<any>>();
  // emits after UI is refreshed
  @Output() updateComplete = new EventEmitter<AgGridEvent>();
  @Output() rowTotalChange = new EventEmitter<GridRowTotalChangeEvent>();
  @Output() dataExistChange = new EventEmitter<GridDataExistEvent>();

  private _selectedRow?: any;
  private _dataSource: SlicedDataSource<any>;
  private ready: boolean = false;
  private api: GridApi;
  private ignoreModelUpdatedEvents: boolean = false;
  private _total: number = -1;

  constructor(@Inject(UI_ERROR_HANDLER) private uiErrorHandler: UiErrorHandler) {

  }

  @Input()
  set dataSource(dataSource: SlicedDataSource<any>) {
    this._dataSource = dataSource;

    if (this.ready) {
      this.reset();
    }
  }

  @Input()
  set selectedRow(row: number) {
    if (row && row !== this._selectedRow) {
      throw new Error('Selection is not supported by infinite scroll');
    }
  }

  enhanceGridOptions(options: GridOptions): void {
    options.defaultColDef = {
      ...options.defaultColDef,
      sortable: isNotNil(this.sorting) ? this.sorting : get(options.defaultColDef, 'sortable', true),
    };
    options.rowModelType = 'infinite';
    options.maxConcurrentDatasourceRequests = 1;
  }

  initGrid(params: GridFeatureInitParams): void {
    // nothing to do
  }

  onGridReady(params: GridFeatureReadyParams): void {
    const { api } = params;

    assign(this, { api });

    this.ready = true;

    api.addEventListener(GRID_EVENT.rowSelected, this.onRowSelected.bind(this));
    api.addEventListener(GRID_EVENT.modelUpdated, (event: AgGridEvent) => {
      if (!this.ignoreModelUpdatedEvents) {
        this.updateComplete.emit(event);
      }
    });

    this.reset();
  }

  onDestroy(): void {
    // nothing to do
  }

  refresh(): void {
    this.ignoreModelUpdatedEvents = true;
    this.api.purgeInfiniteCache();
  }

  private reset(): void {
    const dataSource = this._dataSource;

    if (dataSource) {
      this.api.setGridOption('datasource', this.wrapDataSource(dataSource));
      this.emitRowTotal(-1);
    }
  }

  private wrapDataSource(dataSource: SlicedDataSource<any>): IDatasource {
    const getRows = (params: IGetRowsParams) => {
      const sortModel = params.sortModel[0];

      this.api.setGridOption('loading', true);
      this.ignoreModelUpdatedEvents = true;
      const query = {
        from: params.startRow,
        to: params.endRow,
        sorting: sortModel ? {
          field: sortModel.colId,
          direction: sortModel.sort === 'desc' ? SortDirection.Descending : SortDirection.Ascending,
        } : void 0,
      };
      dataSource.select(query).subscribe(
        (slicedData) => {
          this.ignoreModelUpdatedEvents = false;
          this.api.setGridOption('loading', false);
          if (slicedData.total === 0) {
            this.api.showNoRowsOverlay();
          }
          /** Defer success path so we are not inside AG Grid's draw pass (error #252). */
          setTimeout(() => {
            params.successCallback(slicedData.data, slicedData.total);
            this.emitRowTotal(slicedData.total);
          }, 0);
        },
        (error) => {
          this.ignoreModelUpdatedEvents = false;
          this.api.setGridOption('loading', false);
          setTimeout(() => {
            params.failCallback();
          }, 0);
          this.uiErrorHandler.raise(error);
        },
      );
    };

    return { getRows };
  }

  private onRowSelected({ node }: { node: IRowNode }): void {
    if (node.isSelected()) {
      if (node.id !== this._selectedRow) {
        this._selectedRow = node.id;
        this.selectedRowChange.emit({ id: node.id, node, data: node.data });
      }
    } else if (node.id === this._selectedRow) {
      const ri = node.rowIndex;
      const nextNode = ri != null
        ? (this.api.getDisplayedRowAtIndex(ri) || this.api.getDisplayedRowAtIndex(ri - 1))
        : undefined;
      const eligibleForSelect = (n: IRowNode | undefined): n is IRowNode =>
        n != null && n !== node && n.data != null && n.stub !== true;
      if (eligibleForSelect(nextNode)) {
        nextNode.setSelected(true);
      } else if (isNil(nextNode) || nextNode !== node) {
        this._selectedRow = node.id;
        this.selectedRowChange.emit({});
      }
    }
  }

  private emitRowTotal(total: number): void {
    if (total > 0 && this._total <= 0) {
      this.dataExistChange.emit({ exists: true });
    } else if (total <= 0 && this._total > 0) {
      this.dataExistChange.emit({ exists: false });
    }
    if (this._total !== total) {
      this._total = total;
      this.rowTotalChange.emit({ total });
    }
  }
}
